import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as dayjs from 'dayjs';
import { AnalysisService } from '../../analysis-service.service';
import { Subscription } from 'rxjs';
import { ObjectListService, ObjectNode } from 'src/app/services/object-list.service';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { MatDialog } from '@angular/material/dialog';
import { PlannedDowntimeTableDialog } from './planned-downtime-table-dialog/planned-downtime-table-dialog.component';
import { GeaMesDatepickerModule } from '@gea-mes/datepicker';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';

@Component({
  selector: 'app-planned-downtime-chart',
  templateUrl: './planned-downtime-chart.component.html',
  styleUrls: ['./planned-downtime-chart.component.scss'],
  imports: [GeaMesDatepickerModule, MatTooltip, MatButton, NgIf, NgxEchartsDirective]
})
export class PlannedDowntimeChartComponent implements OnInit, OnDestroy {
  @Input() DatePickerIndex:number = 0;

  objectid:string;
  site:string;
  environment: string;

  chartOptions: EChartsOption = {};

  startDate: string;
  endDate: string;

  downtimedata: any[] = [];
  plannedDTSub$: Subscription;
  disableRawgDataView: boolean = true;

  private chartInstance: any;
  private hoveredSeriesName: string | null = null;
  private hoveredDataIndex: number | null = null;
  hasData: boolean = false;

  private readonly colorPalette: string[] = [
    '#46c7c7',
    '#f57f7f',
    '#5d62b5',
    '#29c3be',
    '#f2726f',
    '#7dcea0',
    '#af7ac5'
  ];

  constructor(
    private route: ActivatedRoute,
    private svcObjectList: ObjectListService,
    private svcAnalysis: AnalysisService,
    private timeFormat: TimeFormatPipe,
    private dialog: MatDialog
  ) {
    this.site = this.route.snapshot.paramMap.get('site');
    this.objectid = this.route.snapshot.paramMap.get('objectid');
  }

  ngOnInit(): void {
    this.getObjectInfo();
  }

  ngOnDestroy(): void {
    if (this.plannedDTSub$){ this.plannedDTSub$.unsubscribe();} 
  }

  getObjectInfo() {
    this.svcObjectList.objectList().subscribe(
      (out) => {
      const plantNode = out.Body.find(plant => plant.search === this.site); // Get PlantNode
      const objectNode:ObjectNode = this.findObjectID(plantNode); // Get ObjectID node

        if (objectNode) {
          this.environment = objectNode.Environment;
        }

        this.getChartData();
      }
    );
  }

  findObjectID(node: ObjectNode): ObjectNode {
    if (!node) return null;

    if (node.ObjectID && node.ObjectID === this.objectid) {
      return node;
    }

    if (node.children) {
      let result: ObjectNode;
      for(let i = 0; result == null && i < node.children.length; i++) {
        result = this.findObjectID(node.children[i]);
      }
      return result;
    } 
    return null;
  }

  onChartInit(ec: any) {
    this.chartInstance = ec;

    ec.on('mouseover', (params: any) => {
      if (params?.componentType === 'series' && params?.seriesType === 'bar') {
        this.hoveredSeriesName = params.seriesName ?? null;
        this.hoveredDataIndex = typeof params.dataIndex === 'number' ? params.dataIndex : null;
      }
    });

    ec.on('mouseout', (params: any) => {
      if (params?.componentType === 'series' && params?.seriesType === 'bar') {
        this.hoveredSeriesName = null;
        this.hoveredDataIndex = null;
      }
    });
  }

  getChartData() {
    this.disableRawgDataView = true;

    if (this.plannedDTSub$) {
      this.plannedDTSub$.unsubscribe();
    }

    this.plannedDTSub$ = this.svcAnalysis
      .plannedDowntime(this.site, this.environment, this.objectid, this.startDate, this.endDate)
      .subscribe({
        next: (out) => {
          this.downtimedata = Array.isArray(out?.Body) ? out.Body : [];
          this.hasData = this.downtimedata.length > 0;
          this.disableRawgDataView = false;
          if (this.hasData) {
            this.buildChartOptions(this.downtimedata);
            } else {
              this.chartOptions = {};
            }
        },
        error: (err) => {
          console.error('plannedDowntime error', err);
          this.downtimedata = [];
          this.hasData = false;
          this.disableRawgDataView = true;
        }
      });
  }

  private buildChartOptions(rows: any[]) {
    const rawCategories: Array<{
      shiftNumber: number;
      shiftDate: string;
      shift: string;
    }> = [];

    const shiftIndexMap = new Map<number, number>();

    rows.forEach((row) => {
      const shiftNumber = Number(row?.ShiftNumber);
      if (!shiftIndexMap.has(shiftNumber)) {
        shiftIndexMap.set(shiftNumber, rawCategories.length);
        rawCategories.push({
          shiftNumber,
          shiftDate: this.safeText(row?.ShiftDate),
          shift: this.safeText(row?.Shift)
        });
      }
    });

    const shouldRotateLabels = rawCategories.length > 20;

    const categories = rawCategories.map((cat) =>
      shouldRotateLabels
        ? `${cat.shiftDate} ${cat.shift}`.trim()
        : `${cat.shiftDate}\n${cat.shift}`.trim()
    );

    const activityColorMap = new Map<string, string>();
    let colorIdx = 0;

    const getActivityName = (row: any): string => {
      const raw = this.safeText(row?.Activity);
      return raw || 'Unknown';
    };

    const getColor = (row: any): string => {
      if (row?.ActivityType === 'PLANNED_OT') {
        return '#a93226';
      }

      const activityName = getActivityName(row);

      if (!activityColorMap.has(activityName)) {
        activityColorMap.set(
          activityName,
          this.colorPalette[colorIdx % this.colorPalette.length]
        );
        colorIdx++;
      }

      return activityColorMap.get(activityName);
    };

    const seriesMap = new Map<string, any>();
    const legendSet = new Set<string>();

    rows.forEach((row) => {
      const categoryIndex = shiftIndexMap.get(Number(row?.ShiftNumber));
      if (categoryIndex == null) return;

      const activityName = getActivityName(row);
      const isOverPlanned = row?.ActivityType === 'PLANNED_OT';

      const legendName = isOverPlanned ? 'Over Planned Time' : activityName;
      const seriesKey = `${row?.DataSetGroup ?? 0}-${row?.DataSetID ?? 0}-${row?.ActivityType ?? 'UNKNOWN'}-${activityName}`;
      const color = getColor(row);
      const tooltipLabel = isOverPlanned
        ? `Over Planned Time: ${activityName}`
        : `Planned Downtime: ${activityName}`;

      legendSet.add(legendName);

      if (!seriesMap.has(seriesKey)) {
        seriesMap.set(seriesKey, {
          name: legendName,
          type: 'bar',
          stack: `group-${row?.DataSetGroup ?? 0}`,
          barWidth: 6,
          barGap: '15%',
          barCategoryGap: '35%',
          itemStyle: {
            color
          },
          data: new Array(categories.length).fill('-')
        });
      }

      const series = seriesMap.get(seriesKey);

      series.data[categoryIndex] = {
        value: Number(row?.Duration ?? 0),
        activity: activityName,
        activityType: row?.ActivityType ?? '',
        durationFormatted: this.timeFormat.convertToString(Number(row?.Duration ?? 0)),
        tooltipLabel,
        itemStyle: {
          color,
          opacity: isOverPlanned ? 1 : ((row?.DataSetID ?? 0) % 2 === 0 ? 0.78 : 0.92)
        }
      };
    });

    const legendData = Array.from(legendSet)
      .filter(Boolean)
      .sort((a, b) => {
        if (a === 'Over Planned Time') return 1;
        if (b === 'Over Planned Time') return -1;
        return a.localeCompare(b);
      });

    const series = Array.from(seriesMap.values())
      .filter(Boolean)
      .map((seriesItem: any) => ({
        ...seriesItem,
        name: seriesItem?.name || 'Unknown',
        emphasis: {
          focus: 'none'
        }
      }));

    const options: EChartsOption = {
      animation: false,
      title: {
        text: 'Planned Downtime',
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 14,
          fontWeight: 'bold',
          color: '#4d4d4d'
        }
      },
      grid: {
        left: 70,
        right: 30,
        top: 70,
        bottom: shouldRotateLabels ? 80 : 100,
        containLabel: true
      },
      legend: {
        bottom: 10,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          color: '#666'
        },
        data: legendData
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        confine: true,
        backgroundColor: '#ffffff',
        borderColor: '#d9dde5',
        borderWidth: 1,
        textStyle: {
          color: '#ccc',
          fontSize: 11
        },
        extraCssText: 'padding: 6px 8px;',
        formatter: (params: any) => {
          const items = (Array.isArray(params) ? params : [params])
            .filter((p: any) => p?.data && p.data.value != null && p.data.value !== '-');

          if (!items.length) return '';

          const categoryLabel = String(items[0]?.axisValue ?? '');

          const lines = [
            `<div style="margin-bottom:6px;">${categoryLabel}</div>`
          ];

          items.forEach((p: any) => {
            const isHovered =
              p.seriesName === this.hoveredSeriesName &&
              p.dataIndex === this.hoveredDataIndex;

            lines.push(`
              <div style="
                margin-top:3px;
                color:${isHovered ? '#000' : '#777'};
              ">
                <span style="
                  display:inline-block;
                  width:8px;
                  height:8px;
                  margin-right:4px;
                  background:${p.color};
                "></span>
                Duration: ${p.data.durationFormatted}<br/>
                <span style="margin-left:16px;">
                  ${p.data.tooltipLabel}
                </span>
              </div>
            `);
          });

          return lines.join('');
        }
      },
      xAxis: {
        type: 'category',
        name: 'Shift Date',
        nameLocation: 'middle',
        nameGap: shouldRotateLabels ? 150 : 95,
        data: categories,
        nameTextStyle: {
          color: '#666'
        },
        axisTick: {
          alignWithLabel: true
        },
        axisLine: {
          lineStyle: {
            color: '#d5dbe3'
          }
        },
        axisLabel: {
          interval: 0,
          rotate: shouldRotateLabels ? 90 : 0,
          fontSize: shouldRotateLabels ? 10 : 12,
          color: '#555',
          margin: shouldRotateLabels ? 12 : 10,
          lineHeight: 16,
          hideOverlap: false,
          formatter: (value: string) => value || ''
        }
      },
      yAxis: {
        type: 'value',
        name: 'Duration (seconds)',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 50,
        min: 0,
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e8edf3'
          }
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#555',
          formatter: (value: number) => this.formatCompactNumber(value)
        }
      },
      series
    };

    this.applyChartOptions(options);
  }


  private applyChartOptions(options: EChartsOption) {
    setTimeout(() => {
      this.chartOptions = { ...options };
    });
  }

  private safeText(value: any): string {
    return value == null ? '' : String(value);
  }

  private formatCompactNumber(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`;
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
    }

    return `${value}`;
  }

  onDateSelection(e: any) {
    this.startDate = dayjs(e.start).format('YYYY-MM-DD');
    this.endDate = dayjs(e.end).format('YYYY-MM-DD');
    this.getChartData();
  }

  openTableView() {
    const dialogRef = this.dialog.open(PlannedDowntimeTableDialog, {
      width: '800px',
      maxHeight: '900px',
      data: {
        objectid: this.objectid,
        site: this.site,
        environment: this.environment,
        data: this.downtimedata
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.debug('openTableView dialog closed', result);
    });
  }
}